<template>
  <div class="container">
    <!-- Sidebar -->
    <div class="sidebar">
      <div class="sidebar-header">
        <h1>Danh sách tin nhắn Zalo</h1>
        <p>Lịch sử Webhook</p>
      </div>

      <div class="sidebar-buttons">
        <button class="btn btn-success ml-2" @click="refreshMessages" :disabled="loading || loadingGroups">
          {{ loading ? 'Đang tải...' : 'Làm mới' }}
        </button>
      </div>

      <!-- Tabs -->
      <div class="tabs">
        <button :class="['tab-btn', { active: activeTab === 'priority' }]" @click="switchTab('priority')">
          Nhóm OA
        </button>
        <button :class="['tab-btn', { active: activeTab === 'other' }]" @click="switchTab('other')">
          Cá nhân
        </button>
      </div>

      <div class="conversations" v-if="conversations.length > 0">
        <button v-for="convId in conversations" :key="convId" @click="selectedConversation = convId"
          :class="['conversation-item', { active: selectedConversation === convId }]">
          <div class="conversation-item-title">{{ getConversationLabel(convId) }}</div>
          <div class="conversation-item-count">
            {{ getConversationMessages(convId).length }} tin nhắn
          </div>
        </button>

        <button v-if="hasMore" class="load-more-btn" @click="loadMoreConversations" :disabled="loadingMore">
          {{ loadingMore ? 'Đang tải...' : 'Xem thêm' }}
        </button>
      </div>

      <div class="empty-state" v-else>
        <p v-if="loading">Đang tải dữ liệu...</p>
        <p v-else-if="error" style="color: #ef4444;">Lỗi: {{ error }}</p>
        <p v-else>Chưa có cuộc trò chuyện</p>
      </div>
    </div>

    <!-- Chat Area -->
    <div class="chat-area">
      <template v-if="selectedConversation">
        <div class="chat-header">
          <h2 style="color:white">Cuộc trò chuyện</h2>
          <p>{{ getConversationLabel(selectedConversation) }}</p>
        </div>

        <div class="messages">
          <template v-if="getConversationMessages(selectedConversation).length > 0">
            <div v-for="(msg, idx) in getConversationMessages(selectedConversation)" :key="idx"
              :class="['message-group', msg.senderType === 'oa' ? 'received' : 'sent']">

              <!-- Avatar + Name -->
              <div class="message-header">
                <img v-if="msg.avatarUrl" :src="msg.avatarUrl" :alt="msg.userName" class="avatar" />
                <div v-else class="avatar-placeholder">{{ msg.userName?.charAt(0)?.toUpperCase() || '?' }}</div>
                <div class="sender-name">{{ msg.userName }}</div>
              </div>

              <!-- Message Bubble -->
              <div class="message-body">
                <div class="message-bubble">
                  <div v-if="msg.imageUrl">
                    <img :src="msg.imageUrl" alt="attachment" class="message-image"
                      @error="(e) => e.target.style.display = 'none'" />
                  </div>
                  <!-- Tin anh kem chu: chu nam duoi hinh -->
                  <div v-if="msg.text" class="message-text">{{ msg.text }}</div>
                </div>
                <div class="message-time">
                  {{ formatTime(msg.timestamp) }}
                </div>
              </div>
            </div>
          </template>
          <div class="empty-chat" v-else>Không có tin nhắn</div>
          <div ref="messagesEnd"></div>
        </div>
      </template>

      <div class="empty-chat" v-else>
        Chọn hoặc tạo cuộc trò chuyện
      </div>
    </div>
  </div>
</template>


<script setup>
/* eslint-disable no-unused-vars */
import { ref, computed, watch, nextTick, onMounted } from 'vue';

// ==================== STATE ====================
const messages = ref([]);
const selectedConversation = ref(null);
const messagesEnd = ref(null);
const loading = ref(false);
const loadingGroups = ref(false);
const error = ref(null);
const activeTab = ref('priority'); // 'priority' or 'other'
const groupMapping = ref({}); // Map recipient -> {groupId, name}

// ==================== PAGINATION ====================
const pageSize = 10;
const currentPage = ref(1);
const hasMore = ref(true);
const loadingMore = ref(false);


// ==================== COMPUTED ====================
const conversations = computed(() => {
  return [...new Set(messages.value.map(m => m.conversationId))];
});


// ==================== METHODS ====================

const loadMessages = async (page = 1, append = false) => {
  if (append) {
    loadingMore.value = true;
  } else {
    loading.value = true;
    error.value = null;
  }

  try {
    const webhookUrl = process.env.VUE_APP_ZALO_WEBHOOK_URL;
    const endpoint = activeTab.value === 'priority'
      ? `${webhookUrl}/ZaloWebHook/history?type=group&page=${page}&pageSize=${pageSize}`
      : `${webhookUrl}/ZaloWebHook/history?type=user&page=${page}&pageSize=${pageSize}`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();

    // Transform API response
    const transformedMessages = [];
    data.forEach(conversation => {
      conversation.messages.forEach(msg => {
        if(msg.event_name.includes('oa_')){
          msg.userName = 'OA';
          msg.avatarUrl = null;
        }
        transformedMessages.push({
          id: msg.id,
          conversationId: conversation.groupName,
          senderId: msg.user_id_by_app,
          recipientId: conversation.recipient,
          appId: msg.app_id,
          eventName: msg.event_name,
          text: msg.message || '',
          imageUrl: msg.imageUrl || null,
          timestamp: parseInt(msg.timestamp),
          senderType: msg.event_name.includes('oa_') ? 'oa' : 'user',
          userName: msg.userName || 'OA',
          avatarUrl: msg.avatarUrl || null
        });
      });
    });

    currentPage.value = page;
    // Còn trang tiếp theo nếu trang này đầy pageSize; rỗng khi "xem thêm" nghĩa là hết thật sự
    hasMore.value = append && transformedMessages.length === 0 ? false : data.length === pageSize;

    if (append) {
      messages.value = [...messages.value, ...transformedMessages];
    } else {
      messages.value = transformedMessages;
      if (messages.value.length > 0) {
        selectedConversation.value = messages.value[0].conversationId;
      }
    }

    // Save to storage
    try {
      const storageKey = activeTab.value === 'priority' ? 'webhook-messages-group' : 'webhook-messages-user';
      await window.storage.set(storageKey, JSON.stringify(messages.value));
    } catch (storageError) {
      console.log('Storage save failed:', storageError);
    }
  } catch (err) {
    console.error('Error loading messages:', err);
    error.value = err.message;
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

const loadMoreConversations = () => {
  if (loadingMore.value || !hasMore.value) return;
  loadMessages(currentPage.value + 1, true);
};

const switchTab = (tab) => {
  activeTab.value = tab;
  selectedConversation.value = null;
  currentPage.value = 1;
  hasMore.value = true;
  loadMessages(1, false);
};

const getConversationLabel = (convId) => {
  // Lấy tên từ mapping, nếu không có thì hiển thị full ID
  if (groupMapping.value[convId]) {
    return groupMapping.value[convId].name;
  }
  return convId; // Hiển thị full ID thay vì truncate
};

const getConversationMessages = (convId) => {
  return messages.value
    .filter(m => m.conversationId === convId)
    .sort((a, b) => a.timestamp - b.timestamp);
};

const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
};

const refreshMessages = () => {
  currentPage.value = 1;
  hasMore.value = true;
  loadMessages(1, false);
};

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesEnd.value) {
      messagesEnd.value.scrollIntoView({ behavior: 'smooth' });
    }
  });
};


// ==================== WATCH ====================
watch(
  () => selectedConversation.value,
  () => {
    scrollToBottom();
  }
);

watch(
  () => messages.value.length,
  () => {
    scrollToBottom();
  }
);


// ==================== LIFECYCLE ====================
onMounted(() => {
  loadMessages(1, false);
});

</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.container {
  display: flex;
  height: 100vh;
  background-color: #f3f4f6;
}

.sidebar {
  width: 450px;
  background: white;
  border-right: 1px solid #d1d5db;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 16px;
  border-bottom: 1px solid #d1d5db;
}

.sidebar-header h1 {
  font-size: 24px;
  font-weight: bold;
  color: #01645A;
}

.sidebar-header p {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

.tabs {
  display: flex;
  border-bottom: 1px solid #d1d5db;
  background: white;
  font-size: 16px;
  font-weight: 600;
}

.tab-btn {
  flex: 1;
  padding: 12px 16px;
  background: none;
  border: none;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
  border-bottom: 2px solid transparent;
}

.tab-btn:hover {
  color: #1f2937;
}

.tab-btn.active {
  color: #01645A;
  border-bottom-color: #01645A;
}

.conversations {
  flex: 1;
  overflow-y: auto;
}

.conversation-item {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  text-align: left;
  background: white;
  border: none;
  width: 100%;
  transition: all 0.2s;
}

.conversation-item:hover {
  background-color: #f9fafb;
}

.conversation-item.active {
  background-color: #eff6ff;
  border-left: 4px solid #01645A;
}

.conversation-item-title {
  font-weight: 600;
  color: #1f2937;
  word-break: break-word;
  white-space: normal;
  overflow: visible;
}

.conversation-item-count {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

.empty-state {
  padding: 16px;
  text-align: center;
  color: #9ca3af;
}

.load-more-btn {
  width: 100%;
  padding: 12px 16px;
  background: white;
  border: none;
  border-top: 1px solid #e5e7eb;
  color: #01645A;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.load-more-btn:hover:not(:disabled) {
  background-color: #f9fafb;
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.empty-state p {
  margin: 8px 0;
  font-size: 14px;
}

.sidebar-buttons {
  padding: 16px;
  border-top: 1px solid #d1d5db;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn {
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  color: white;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  background-color: #10b981;
}

.btn-success:hover:not(:disabled) {
  background-color: #059669;
}

.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
}

.chat-header {
  padding: 16px;
  background: linear-gradient(135deg, #01645A 0%, #01645A 100%);
  color: white;
  border-bottom: 1px solid #d1d5db;
}

.chat-header h2 {
  font-size: 18px;
  font-weight: bold;
}

.chat-header p {
  font-size: 12px;
  opacity: 0.9;
  margin-top: 4px;
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-group {
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
  gap: 4px;
  margin-bottom: 8px;
}

.message-group.sent {
  align-items: flex-end;
}

.message-group.received {
  align-items: flex-start;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
}

.message-group.sent .message-header {
  flex-direction: row-reverse;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #01645A;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
}

.sender-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.message-body {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 0 12px;
}

.message-group.sent .message-body {
  flex-direction: row-reverse;
}

.message-bubble {
  padding: 12px 16px;
  border-radius: 12px;
  word-wrap: break-word;
  max-width: 70%;
}

.message-group.sent .message-bubble {
  background-color: #01645A;
  color: white;
  border-bottom-right-radius: 0;
}

.message-group.received .message-bubble {
  background-color: #e5e7eb;
  color: #1f2937;
  border-bottom-left-radius: 0;
}

.message-image {
  max-width: 100%;
  border-radius: 8px;
  max-height: 300px;
  object-fit: cover;
}

/* Chi tach dong khi bubble co ca hinh lan chu */
.message-bubble > div + .message-text {
  margin-top: 8px;
}

.message-time {
  font-size: 11px;
  opacity: 0.7;
  white-space: nowrap;
}

.empty-chat {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 16px;
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>